import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

const GalleryWrap = styled.div`
  width: 100%;
  max-width: 500px;
  margin-bottom: 2rem;
`;

const MainStage = styled.div`
  width: 100%;
  height: ${(p) => p.$height}px;
  border: none;
  padding: 0;
  border-radius: 0.5rem;
  background: ${(p) => (p.$beige ? '#f6f1e8' : '#a8e6cf')};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  touch-action: pan-y;
  user-select: none;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: ${(p) => (p.$beige ? '#f6f1e8' : '#a8e6cf')};
    pointer-events: none;
  }
`;

const ZoomButton = styled.button`
  position: absolute;
  inset: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  padding: 0;
  touch-action: pan-y;
  z-index: 1;
`;

const StageNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.$dir === 'prev' ? 'left: 0.4rem;' : 'right: 0.4rem;')}
  z-index: 2;
  border: none;
  background: rgba(15, 23, 42, 0.45);
  color: #fff;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  display: ${(p) => (p.$hidden ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;

  @media (max-width: 640px) {
    width: 2rem;
    height: 2rem;
    font-size: 1.2rem;
  }
`;

const Placeholder = styled.div`
  width: 100%;
  height: ${(p) => p.$height}px;
  border-radius: 0.5rem;
  background: var(--bg-secondary, #f1f5f9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: var(--text-light, #94a3b8);
`;

const CounterBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  pointer-events: none;
  z-index: 2;
`;

const ThumbRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
`;

const ThumbButton = styled.button`
  flex: 0 0 72px;
  width: 72px;
  height: 72px;
  border-radius: 0.375rem;
  border: 2px solid ${(p) => (p.$active ? 'var(--primary-color, #667eea)' : 'transparent')};
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: #f1f5f9;
  position: relative;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VideoBadge = styled.span`
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 0.65rem;
  padding: 2px 5px;
  border-radius: 4px;
  font-weight: 700;
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  touch-action: pan-y;
`;

const LightboxImage = styled.img`
  max-width: min(96vw, 1200px);
  max-height: 90vh;
  object-fit: contain;
`;

const LightboxVideo = styled.video`
  max-width: min(96vw, 1200px);
  max-height: 90vh;
  object-fit: contain;
  background: #000;
`;

const LightboxClose = styled.button`
  position: fixed;
  top: 1rem;
  right: 1rem;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  font-size: 1.25rem;
  cursor: pointer;
`;

const LightboxNav = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.$dir === 'prev' ? 'left: 1rem;' : 'right: 1rem;')}
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 999px;
  font-size: 1.5rem;
  cursor: pointer;
  display: ${(p) => (p.$hidden ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
`;

const SWIPE_THRESHOLD_PX = 40;

function normalizeMedia(images = [], media = null) {
  if (Array.isArray(media) && media.length) {
    return media
      .map((entry) => {
        if (typeof entry === 'string') return { url: entry, mediaType: 'image' };
        if (entry?.url) {
          return {
            url: entry.url,
            mediaType: entry.mediaType === 'video' ? 'video' : 'image',
          };
        }
        return null;
      })
      .filter(Boolean);
  }
  return (images || []).filter(Boolean).map((url) => ({ url, mediaType: 'image' }));
}

const ItemImageGallery = ({ images = [], media = null, alt = 'Item photo', height = 400, beige = false }) => {
  const items = useMemo(() => normalizeMedia(images, media), [images, media]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    setActiveIndex(0);
  }, [items]);

  const goPrev = useCallback(() => {
    setActiveIndex((idx) => (idx > 0 ? idx - 1 : items.length - 1));
  }, [items.length]);

  const goNext = useCallback(() => {
    setActiveIndex((idx) => (idx < items.length - 1 ? idx + 1 : 0));
  }, [items.length]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setLightboxOpen(false);
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxOpen, goPrev, goNext]);

  const onPointerDown = (event) => {
    if (items.length < 2) return;
    const point = event.touches?.[0] || event;
    touchStartX.current = point.clientX;
    touchStartY.current = point.clientY;
    suppressClickRef.current = false;
  };

  const onPointerUp = (event) => {
    if (items.length < 2 || touchStartX.current == null) return;
    const point = event.changedTouches?.[0] || event;
    const dx = point.clientX - touchStartX.current;
    const dy = point.clientY - (touchStartY.current || point.clientY);
    touchStartX.current = null;
    touchStartY.current = null;

    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) {
      return;
    }

    suppressClickRef.current = true;
    if (dx < 0) goNext();
    else goPrev();
  };

  if (!items.length) {
    return (
      <GalleryWrap>
        <Placeholder $height={height} aria-hidden>
          📦
        </Placeholder>
      </GalleryWrap>
    );
  }

  const active = items[activeIndex] || items[0];
  const isVideo = active.mediaType === 'video';

  return (
    <GalleryWrap>
      <MainStage
        $height={height}
        $beige={beige}
        onTouchStart={onPointerDown}
        onTouchEnd={onPointerUp}
        onMouseDown={onPointerDown}
        onMouseUp={onPointerUp}
      >
        {isVideo ? (
          <video src={active.url} controls playsInline preload="metadata" aria-label={alt} />
        ) : (
          <>
            <img src={active.url} alt={alt} draggable={false} />
            <ZoomButton
              type="button"
              onClick={() => {
                if (suppressClickRef.current) {
                  suppressClickRef.current = false;
                  return;
                }
                setLightboxOpen(true);
              }}
              aria-label={`View ${alt} photo ${activeIndex + 1} of ${items.length}`}
            />
          </>
        )}
        {items.length > 1 && (
          <>
            <StageNav
              type="button"
              $dir="prev"
              aria-label="Previous photo"
              onClick={(event) => {
                event.stopPropagation();
                goPrev();
              }}
            >
              ‹
            </StageNav>
            <StageNav
              type="button"
              $dir="next"
              aria-label="Next photo"
              onClick={(event) => {
                event.stopPropagation();
                goNext();
              }}
            >
              ›
            </StageNav>
            <CounterBadge>
              {activeIndex + 1} / {items.length}
            </CounterBadge>
          </>
        )}
      </MainStage>

      {items.length > 1 && (
        <ThumbRow aria-label="Media thumbnails">
          {items.map((item, index) => (
            <ThumbButton
              key={`${item.url}-${index}`}
              type="button"
              $active={index === activeIndex}
              onClick={() => setActiveIndex(index)}
              aria-label={`Show media ${index + 1}`}
            >
              {item.mediaType === 'video' ? (
                <>
                  <video src={item.url} muted preload="metadata" />
                  <VideoBadge>VIDEO</VideoBadge>
                </>
              ) : (
                <img src={item.url} alt="" />
              )}
            </ThumbButton>
          ))}
        </ThumbRow>
      )}

      {lightboxOpen && !isVideo && (
        <LightboxOverlay
          role="dialog"
          aria-modal="true"
          aria-label={`${alt} photos`}
          onClick={() => setLightboxOpen(false)}
          onTouchStart={onPointerDown}
          onTouchEnd={(event) => {
            onPointerUp(event);
          }}
        >
          <LightboxClose type="button" aria-label="Close" onClick={() => setLightboxOpen(false)}>
            ×
          </LightboxClose>
          {items.length > 1 && (
            <>
              <LightboxNav
                type="button"
                $dir="prev"
                aria-label="Previous"
                onClick={(event) => {
                  event.stopPropagation();
                  goPrev();
                }}
              >
                ‹
              </LightboxNav>
              <LightboxNav
                type="button"
                $dir="next"
                aria-label="Next"
                onClick={(event) => {
                  event.stopPropagation();
                  goNext();
                }}
              >
                ›
              </LightboxNav>
            </>
          )}
          {(items[activeIndex] || active).mediaType === 'video' ? (
            <LightboxVideo
              src={(items[activeIndex] || active).url}
              controls
              autoPlay
              onClick={(event) => event.stopPropagation()}
            />
          ) : (
            <LightboxImage
              src={(items[activeIndex] || active).url}
              alt={alt}
              onClick={(event) => event.stopPropagation()}
            />
          )}
        </LightboxOverlay>
      )}
    </GalleryWrap>
  );
};

export default ItemImageGallery;
